import React, { useState, useEffect, useRef } from 'react';
import { fetchResumeData } from '../../../fetchProjects';

const CoreCompetencies = () => {
  const [competencies, setCompetencies] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAllTech, setShowAllTech] = useState(false);
  const [showAllSoft, setShowAllSoft] = useState(false);
  const techRef = useRef(null);
  const softRef = useRef(null);

  useEffect(() => {
    const fetchCompetencies = async () => {
      try {
        const data = await fetchResumeData();
        const filtered = data.filter(item => item.type === 'competency');
        if (filtered.length > 0) {
          setCompetencies(filtered);
        } else {
          setCompetencies([]);
        }
      } catch (err) {
        console.error('Failed to fetch competencies:', err);
        setCompetencies([]);
      } finally {
        setLoading(false);
      }
    };
    fetchCompetencies();
  }, []);

  const technical = competencies ? competencies.filter(c => c.badge?.toLowerCase() === 'technical') : [];
  // Flatten soft skills if multiple items entered, or just take from the first "soft" item
  const soft = competencies 
    ? competencies.filter(c => c.badge?.toLowerCase() === 'soft').flatMap(c => c.technologies || c.achievements || [])
    : [];

  const visibleTech = showAllTech ? technical : technical.slice(0, 5);
  const visibleSoft = showAllSoft ? soft : soft.slice(0, 8);

  const handleToggleTech = () => {
    if (showAllTech && techRef.current) {
      const yOffset = -100;
      const y = techRef.current.getBoundingClientRect().top + window.scrollY + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
    setShowAllTech(!showAllTech);
  };

  const handleToggleSoft = () => {
    if (showAllSoft && softRef.current) {
      const yOffset = -100;
      const y = softRef.current.getBoundingClientRect().top + window.scrollY + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
    setShowAllSoft(!showAllSoft);
  };

  if (!loading && (!competencies || competencies.length === 0)) return null;

  return (
    <section className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl p-6 lg:p-8 mb-8 relative overflow-hidden shadow-2xl">
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5 opacity-30"></div>
      <h2 className="font-heading font-bold text-2xl lg:text-3xl text-text-primary mb-6 relative z-10">
        Space-Tech Competencies
      </h2>
      {loading ? (
        <div className="grid lg:grid-cols-2 gap-8 animate-pulse">
          <div className="space-y-4">
            <div className="h-6 bg-white/10 rounded w-1/2 mb-6"></div>
            {[1, 2, 3].map(i => (
               <div key={i}>
                 <div className="h-4 bg-white/5 rounded w-1/3 mb-2"></div>
                 <div className="flex gap-2">
                    <div className="h-6 bg-white/5 rounded w-16"></div>
                    <div className="h-6 bg-white/5 rounded w-20"></div>
                 </div>
               </div>
            ))}
          </div>
          <div className="space-y-4">
            <div className="h-6 bg-white/10 rounded w-1/2 mb-6"></div>
            {[1, 2, 3, 4].map(i => (
               <div key={i} className="flex gap-2 items-center">
                  <div className="w-2 h-2 bg-white/10 rounded-full"></div>
                  <div className="h-4 bg-white/5 rounded w-3/4"></div>
               </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-8 lg:gap-12 relative z-10">
          {/* Technical Skills */}
          <div ref={techRef}>
            <h3 className="font-heading font-semibold text-xl text-text-primary mb-4">
              Research & Outreach Skills
            </h3>
            <div className="space-y-4">
              {visibleTech.map((tech, index) => (
                <div key={index}>
                  <h4 className="font-semibold text-accent mb-2">{tech.title}</h4>
                  <div className="flex flex-wrap gap-2">
                    {tech.technologies && tech.technologies.map((skill, skillIndex) => (
                      <span
                        key={skillIndex}
                        className="bg-white/5 px-3 py-1 rounded-full text-sm text-text-secondary border border-white/5 group-hover:bg-white/10 transition-smooth"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            {technical.length > 5 && (
              <button 
                onClick={handleToggleTech}
                className="mt-6 text-accent hover:text-accent-light text-sm font-semibold transition-colors"
                aria-label={showAllTech ? 'Show fewer technical skills' : 'Show more technical skills'}
                title="Toggle Technical Skills"
              >
                {showAllTech ? 'Show Less' : `Read More (${technical.length - 5} More)`}
              </button>
            )}
          </div>

          {/* Soft Skills */}
          <div ref={softRef}>
            <h3 className="font-heading font-semibold text-xl text-text-primary mb-4">
              Leadership & Collaboration
            </h3>
            <div className="grid grid-cols-1 gap-3">
              {visibleSoft.map((skill, index) => (
                <div key={index} className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-success-light rounded-full flex-shrink-0 shadow-[0_0_8px_rgba(34,197,94,0.3)]"></div>
                  <span className="text-text-secondary">{skill}</span>
                </div>
              ))}
            </div>
            {soft.length > 8 && (
              <button 
                onClick={handleToggleSoft}
                className="mt-6 text-accent hover:text-accent-light text-sm font-semibold transition-colors"
                aria-label={showAllSoft ? 'Show fewer soft skills' : 'Show more soft skills'}
                title="Toggle Soft Skills"
              >
                {showAllSoft ? 'Show Less' : `Read More (${soft.length - 8} More)`}
              </button>
            )}
          </div>
        </div>
      )}
    </section>
  );
};

export default CoreCompetencies;